import { Badge, Box, Button, Flex, ListItem, UnorderedList, useDisclosure } from '@chakra-ui/react';
import { faCheckSquare, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { JSX, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { CustomHeading, Span } from 'shared-components';
import {
  ApplicationStatus,
  GenericsIntegration,
  GradeLevel,
  GradeSystem,
  ResultStatus,
  IQualification,
  IStudy,
  ApplicantOfferRequirementTask,
  IAcademicResultFulfilmentData,
  IApplicantOffer,
} from 'types';
import { ApplicationState } from '../../../../store';
import ApplicantHeader from '../../../molecules/applicantHeader';
import { generateFlowSpecificUrl } from '@/components/molecules/SectionHeader/generateFlowSpecificUrl';
import ShowAlerts from './show-alert';
import PotentialDuplicatesModal from '@/components/pages/modals/potentialDuplicatesModal';
import { updateDuplicate } from '@/store/apply/actions';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';
import { useTranslation } from "react-i18next";

function formatDateMonthYear(date: Date): string {
  if (!date) {
    return 'Present';
  }
  return `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
}

/**
 * Get the most recent study history from Academic Results requirements across all offers.
 * When an applicant updates their academic results via the Programme page, the data is stored
 * in the offer requirement's fulfilmentData. This function retrieves that data to ensure
 * the Summary page shows the most up-to-date academic qualification information.
 */
function getStudyHistoryFromOfferRequirements(offers: IApplicantOffer[]): IStudy | undefined {
  if (!offers || offers.length === 0) {
    return undefined;
  }

  // Collect all Academic Results tasks with fulfilment data from all offers
  const academicResultsTasks = offers.flatMap(offer => {
    const tasks = getAllTasksFromRequirements(offer.requirements);
    return tasks.filter(
      task =>
        task.type === ApplicantOfferRequirementTask.AcademicResults &&
        (task.fulfilmentData as IAcademicResultFulfilmentData)?.studyHistory,
    );
  });

  if (academicResultsTasks.length === 0) {
    return undefined;
  }

  // Use the first one found (all offers should have the same applicant's academic data)
  const { studyHistory } = academicResultsTasks[0].fulfilmentData as IAcademicResultFulfilmentData;
  if (!studyHistory) {
    return undefined;
  }

  return studyHistory;
}

export default function Summary(): JSX.Element {
    const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const params = useParams();
  const { uni, userId, applicationId } = params;
  const { application, applicant, agentAccessStatus, offers } = useSelector((state: ApplicationState) => state.apply);
  const integration = useSelector((state: ApplicationState) => {
    if (state.config?.sits?.sitsApplyEnabled) {
      return GenericsIntegration.Integrations.SITS;
    }

    if (state.config?.ellucian?.ellucianEnabled) {
      return GenericsIntegration.Integrations.ELLUCIAN;
    }

    if (state.config?.oracle?.oracleEnabled) {
      return GenericsIntegration.Integrations.ORACLE;
    }

    return '';
  });
  const { duplicateMatches, potentialDuplicate } = applicant;

  const { studyHistory, hasEnglishTest, englishTest } = application;

  const isNotSubmitted = [ApplicationStatus.InProgress, ApplicationStatus.Completed].includes(
    application.applicationStatus,
  );

  const navigate = useNavigate();

  const { isOpen: isDuplicatesOpen, onOpen: onDuplicatedOpen, onClose: onDuplicatedClose } = useDisclosure();

  // Prefer study history from offer requirements (updated when applicant edits academic results)
  // Fall back to application.studyHistory.highest (original data from when offer was created)
  const highestStudy = useMemo(() => {
    if (isNotSubmitted) {
      return {} as IStudy;
    }

    // Try to get from offer requirements first (most up-to-date)
    const fromOfferRequirements = getStudyHistoryFromOfferRequirements(offers);
    if (fromOfferRequirements) {
      return fromOfferRequirements;
    }

    // Fall back to application.studyHistory.highest
    if (!studyHistory) {
      return {} as IStudy;
    }
    return studyHistory.highest ?? ({} as IStudy);
  }, [isNotSubmitted, offers, studyHistory]);

  const isIncomplete = isNotSubmitted || (!studyHistory && !getStudyHistoryFromOfferRequirements(offers));

  const onEditDetails = (): void => {
    navigate('../applicant-details');
  };
  const tryAgainHandler = (): void => {
    dispatch(updateDuplicate({ uni, userId, applicationId, integration, isDuplicate: true }));
  };

  const onOpenApplication = (): void => {
    const route = generateFlowSpecificUrl({
      url: '/',
      params: {
        uni: application.universityShortcode,
        applicantId: application.userId,
      },
      currentView: 'staff',
    });
    window.location.href = route;
  };

  return (
    <>
      <ApplicantHeader>
        <Box mb={8}>
          <Flex mb={2} flexWrap={'wrap'} rowGap={4} columnGap={3}>
            <CustomHeading data-selector="summaryHeader-name" level="h1" flexGrow={1} mb={0} wordBreak={'break-word'}>
              <Span fontWeight={'bold'}>
                {application.givenName
                  ? `${application.givenName} ${application.familyName ?? ''}`
                  : application.knownBy}
              </Span>
            </CustomHeading>
          </Flex>

          <ShowAlerts
            application={application}
            applicant={applicant}
            agentAccessStatus={agentAccessStatus}
            onEditDetails={onEditDetails}
            onOpenApplication={onOpenApplication}
            onDuplicateDetails={onDuplicatedOpen}
            onTryAgain={tryAgainHandler}
          />
        </Box>
      </ApplicantHeader>

      {!isIncomplete && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" gap={1.5}>
              <Box pl={0.5} pr={0.5}>
                <FontAwesomeIcon icon={faCheckSquare} width={16} className="utility-icon" />
              </Box>
              <Box color="vivid">{t('common:highestQualification')}</Box>
            </Box>
            <Button
              as={RouterLink}
              to="../sections"
              size={'sm'}
              rightIcon={<FontAwesomeIcon icon={faChevronRight} className="utility-icon" />}
              variant={'ghost'}
            >
              {t('common:application')}
                                      </Button>
          </Box>
          <Box>
            <Box borderRadius="xl" px={6} py={4} borderWidth={1} mb={3} gap={4}>
              <Box display="flex" justifyContent="space-between" mb={4}>
                <Box fontSize="lg">{highestStudy?.institutionName || 'Unknown institution'}</Box>
              </Box>

              <Box display="flex" gap={2}>
                <Box fontSize="xs" color="muted" mb={2}>
                  {t('common:qualifications')}
                                                  </Box>
                <Badge height="full" variant="pill" fontSize="xs">
                  {Object.keys(highestStudy?.qualifications ?? {}).length}
                </Badge>
              </Box>

              <UnorderedList spacing={2}>
                {Object.entries(highestStudy?.qualifications ?? {}).map(([id, q]: [string, IQualification]) => {
                  // Use the qualification type label (e.g., "Master's degree")
                  const qualificationLabel =
                    q?.qualificationTypeLabel ||
                    q?.otherQualificationName ||
                    q?.levelLabel ||
                    (q?.level && GradeLevel[q.level as keyof typeof GradeLevel]) ||
                    q?.level ||
                    'Unknown qualification';
                  // Include subject in the display if it exists
                  const displayLabel = q?.subject ? `${qualificationLabel} in ${q.subject}` : qualificationLabel;
                  const gradeSystemLabel =
                    q?.gradeSystemLabel ||
                    (q?.gradeSystem && GradeSystem[q.gradeSystem as keyof typeof GradeSystem]) ||
                    q?.gradeSystem ||
                    'N/A';
                  // Format grade display based on result status
                  const gradeDisplay =
                    highestStudy?.resultStatus === ResultStatus.InterimResults
                      ? `${q?.grade || 'N/A'} (predicted)`
                      : `${q?.grade || 'N/A'} / ${gradeSystemLabel}`;
                  return (
                    <ListItem key={id}>
                      <Flex justifyContent="space-between" alignItems="center">
                        <Box>{displayLabel}</Box>
                        <Flex gap={4} alignItems="center">
                          {q?.graduationDate && (
                            <Box fontSize="sm" color="muted">
                              {formatDateMonthYear(new Date(q.graduationDate))}
                            </Box>
                          )}
                          <Box>{gradeDisplay}</Box>
                        </Flex>
                      </Flex>
                    </ListItem>
                  );
                })}
              </UnorderedList>
            </Box>

            {hasEnglishTest && englishTest.hasTakenTest && (
              <Box borderRadius="xl" px={6} py={4} borderWidth={1} gap={4}>
                <Box fontSize="lg">{englishTest.type}</Box>
                <Box>{englishTest.score}</Box>
              </Box>
            )}
          </Box>
        </>
      )}

      {potentialDuplicate && duplicateMatches?.length > 0 && (
        <PotentialDuplicatesModal
          isOpen={isDuplicatesOpen}
          onClose={onDuplicatedClose}
          duplicateStudents={duplicateMatches}
          integration={integration}
        />
      )}
    </>
  );
}
